import { useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { BrandLogo } from "@/components/BrandLogo";
import { authClient, authErrorMessage } from "@/lib/auth-client";
import { useInitialFocus } from "@/hooks/useInitialFocus";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const emailRef = useInitialFocus<HTMLInputElement>();

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    const { error: requestError } = await authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);
    if (requestError) {
      setError(authErrorMessage(requestError, "Could not request a password reset."));
      return;
    }
    setSubmitted(true);
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <BrandLogo className="auth-logo" size={22} />
        <h1 className="auth-title">Reset your password</h1>
        <p className="auth-subtitle">
          {submitted ? "If an account exists for that address, a reset link is on its way." : "Enter your account email and we'll send you a reset link."}
        </p>

        {!submitted ? (
          <form className="panel" onSubmit={handleSubmit}>
            <div className="field">
              <label className="label" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                className="input"
                type="email"
                ref={emailRef}
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
            <button className="btn btn-primary btn-block" type="submit" disabled={loading}>
              {loading ? "Sending…" : "Send reset link"}
            </button>
            {error && <div className="error-text">{error}</div>}
          </form>
        ) : (
          <div className="panel auth-success-state" role="status">
            <div className="auth-state-icon">✓</div>
            <strong>Check your inbox</strong>
            <span>A reset link was sent to {maskEmail(email)}.</span>
            <button className="btn btn-ghost" type="button" onClick={() => setSubmitted(false)}>Send again</button>
          </div>
        )}

        <p className="auth-footer">
          <Link to="/login">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}

function maskEmail(email: string) {
  const [name, domain] = email.split("@");
  return `${name?.slice(0, 2) ?? ""}•••@${domain ?? ""}`;
}
